import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';

import { Attachment } from '@core/models';
import { AttachmentApiService } from '@core/services';
import { OrderService } from '@core/services/order.service';
import { InventoryService } from '@core/services/inventory.service';
import { ShipmentService } from '@core/services/shipment.service';

interface AttachmentResource {
  listAttachments(resourceId: string): Observable<Attachment[]>;
  createAttachment(resourceId: string, file: File): Observable<Attachment>;
}

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  private readonly serviceMap: Record<string, Pick<AttachmentResource, 'listAttachments' | 'createAttachment'>>;

  constructor(
    private readonly attachmentApi: AttachmentApiService,
    private readonly orderService: OrderService,
    private readonly inventoryService: InventoryService,
    private readonly shipmentService: ShipmentService,
  ) {
    this.serviceMap = {
      'order': this.orderService,
      'inventory': this.inventoryService,
      'shipment': this.shipmentService,
    };
  }

  private getService(resource: string): AttachmentResource {
    const service = this.serviceMap[resource];
    if (!service) throw new Error(`No service found for resource type: ${resource}`);
    return service;
  }

  list(resource: string, resourceId: string): Observable<Attachment[]> {
    return this.getService(resource).listAttachments(resourceId);
  }

  create(resource: string, resourceId: string, file: File): Observable<Attachment[]> {
    return this.getService(resource).createAttachment(resourceId, file)
      .pipe(switchMap(() => this.list(resource, resourceId)));
  }

  delete(resource: string, resourceId: string, attachmentId: string): Observable<Attachment[]> {
    return this.attachmentApi.delete(attachmentId).pipe(switchMap(() => this.list(resource, resourceId)));
  }
}
